import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD = 50;
const DRAG_MAX = 200;
const AUTO_PLAY_INTERVAL_MS = 6000;

/**
 * Hero carousel state and drag-to-navigate logic.
 * @param {number} slideCount - Number of slides
 * @returns {{ heroIndex, setHeroIndex, goPrev, goNext, dragOffsetPx, isDragging, handlers, trackStyle, progress }}
 */
export function useHeroCarousel(slideCount) {
  const [heroIndex, setHeroIndex] = useState(0);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const dragStartXRef = useRef(0);
  const dragOffsetPxRef = useRef(0);
  const progressStartRef = useRef(Date.now());

  const goPrev = useCallback(() => {
    if (slideCount <= 1) return;
    setHeroIndex((i) => (i + slideCount - 1) % slideCount);
    setProgress(0);
    progressStartRef.current = Date.now();
  }, [slideCount]);

  const goNext = useCallback(() => {
    if (slideCount <= 1) return;
    setHeroIndex((i) => (i + 1) % slideCount);
    setProgress(0);
    progressStartRef.current = Date.now();
  }, [slideCount]);

  const setHeroIndexWithReset = useCallback((indexOrUpdater) => {
    setHeroIndex(indexOrUpdater);
    setProgress(0);
    progressStartRef.current = Date.now();
  }, []);

  const handleDragStart = useCallback((e) => {
    if (e.target.closest("button")) return;
    setIsDragging(true);
    dragStartXRef.current = e.touches ? e.touches[0].clientX : e.clientX;
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const delta = dragStartXRef.current - x;
    const clamped = Math.max(-DRAG_MAX, Math.min(DRAG_MAX, delta));
    dragOffsetPxRef.current = clamped;
    setDragOffsetPx(clamped);
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    const current = dragOffsetPxRef.current;
    setIsDragging(false);
    setDragOffsetPx(0);
    if (current > DRAG_THRESHOLD) goNext();
    else if (current < -DRAG_THRESHOLD) goPrev();
  }, [isDragging, goNext, goPrev]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => handleDragMove(e);
    const onEnd = () => handleDragEnd();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (slideCount <= 1 || isPaused) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const p = Math.min(100, (elapsed / AUTO_PLAY_INTERVAL_MS) * 100);
      setProgress(p);
      if (p >= 100) {
        goNext();
      }
    }, 200);
    return () => clearInterval(interval);
  }, [slideCount, isPaused, heroIndex, goNext]);

  const trackStyle = {
    transform: `translateX(calc(-${heroIndex * (100 / slideCount)}% - ${dragOffsetPx}px))`,
    transition: isDragging
      ? "none"
      : "transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  };

  return {
    heroIndex,
    setHeroIndex: setHeroIndexWithReset,
    goPrev,
    goNext,
    dragOffsetPx,
    isDragging,
    progress,
    isPaused,
    setIsPaused,
    handlers: {
      onMouseDown: handleDragStart,
      onTouchStart: handleDragStart,
      onTouchMove: handleDragMove,
      onTouchEnd: handleDragEnd,
    },
    trackStyle,
  };
}
